<template>
  <FoodleapSimplifiedActorDetail v-if="shouldShowAugmentedProfile"/>
  <SimplifiedActorDetail v-else-if="shouldShowActorSimplifiedDetailPage || this.isProduct"/>
  <ActorDetail v-else/>
</template>

<script lang="ts">
  import ActorDetail from './ActorDetail.vue'
  import FoodleapSimplifiedActorDetail from './FoodleapSimplifiedActorDetail.vue'
  import SimplifiedActorDetail from './SimplifiedActorDetail.vue'
  import UiMixin from '../../util/UiMixin.js'
  import { defineComponent } from 'vue'

  export default defineComponent({
    components: { FoodleapSimplifiedActorDetail, SimplifiedActorDetail, ActorDetail },
    mixins: [UiMixin],
    computed: {
      currentActor() {
        // This object always contains the data for the current view actor detail page
        return this.$store.getters.detailActor
      },
      isProduct() {
        return this.currentActor && this.currentActor['actor_type'] === 'Product'
      },
      /**
       * On Foodleap the Augmented Actor Profile *is* the actor detail page for the people it was
       * written for — actors and anonymous visitors. The page gates its members-only sections
       * itself rather than being kept behind a route guard.
       *
       * Three ways out of it, all deliberate:
       *   · Ecosystem owners and members default to the classic page. The augmented profile is an
       *     outward-facing story; their job needs the database entry behind it.
       *   · `?profile=classic` — the "Classic profile" button in the augmented profile's top bar.
       *     Owners only there, but the query works for anyone who has the link.
       *   · Products keep the classic page: the augmented profile is written about organisations
       *     (transitions, roles, capabilities), and none of it applies to a product.
       *
       * Every other ecosystem is unchanged; /actors/:id/aap remains the preview route for them.
       *
       * ⚠ Not live yet — see the AAP ROLLOUT block in the body. The paragraphs above describe the
       * finished behaviour, which is what remains once that block is deleted. Grep `AAP ROLLOUT`
       * for every place the limited release is held back.
       */
      shouldShowAugmentedProfile() {
        if (!this.$store.getters.isFoodleap || this.isProduct) {
          return false
        }

        // Owners and members keep the classic page as their default, the same way
        // shouldShowActorSimplifiedDetailPage already excludes them from the simplified one. It
        // carries the tooling they actually work in — notes, changes, suggestions, duplicates,
        // relevancy, the edit panel — none of which the augmented profile has or should have. They
        // reach the augmented profile from the "Augmented profile" button in the classic top bar.
        if (this.isOwner || this.isMember) {
          return false
        }

        // AAP ROLLOUT · limited release — delete this block to ship the behaviour described above.
        // Until the augmented profile opens up, /actors/:id keeps serving the classic page to
        // everyone except the people reviewing it: @datascouts.eu and @foodvalley.nl, via the
        // allowlist in the `canPreviewAugmentedProfile` getter. Nothing else about the page is
        // conditional on the rollout — this one check is the whole gate.
        if (!this.$store.getters.canPreviewAugmentedProfile) {
          return false
        }

        return this.$route.query.profile !== 'classic'
      },
    },
  })
</script>
